import { useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import { EmptyState } from '../../components/ui/EmptyState'
import { Skeleton } from '../../components/ui/Skeleton'
import { Table } from '../../components/ui/Table'
import { filterRowsByCarriedFacets } from './catalog-carried-facet-filter'
import { facetSearchParams, parseFacetSelection } from './catalog-series-facet-filter'
import {
  type SeriesFilterSelection,
  deriveSeriesFacets,
  dimensionCellValue,
  filterSeriesRows,
} from './catalog-series-filters'
import { usesCardConfiguratorLayout } from './catalogTaxonomy'
import { CarriedFacetBanner } from './components/CarriedFacetBanner'
import { CatalogSeriesFilterSidebar } from './components/CatalogSeriesFilterSidebar'
import { ConfiguratorCore } from './components/ConfiguratorCore'
import { ResultCard } from './components/ResultCard'
import { gateConfigSchema } from './configurator-sku'
import { resultDisplayName } from './format'
import type { CatalogResultCard } from './types'
import { useCatalogSeries } from './useCatalogSeries'
import { useCatalogSeriesRows } from './useCatalogSeriesRows'
import { recordCatalogRowView } from './useRecentlyViewed'

const EMPTY_SELECTION: SeriesFilterSelection = {
  standard: false,
  configurable: false,
  ranges: new Map(),
  enums: new Map(),
}

export interface CatalogSeriesConfigurePageProps {
  /** Injectable for tests; defaults to the real BFF-backed series-scoped search. */
  readonly service?: Parameters<typeof useCatalogSeriesRows>[1]
  /**
   * Injectable for tests; defaults to the real BFF-backed series-list seam
   * (`GET /catalog/categories/{category}/series`, the same one
   * `CatalogSeriesPage` already uses) — UID-521. This page uses it ONLY to
   * resolve `heading` to the real per-series `CatalogSeries.name`, never to
   * render a series grid.
   */
  readonly seriesListService?: Parameters<typeof useCatalogSeries>[2]
}

/**
 * A row's stable identity for the expand toggle / React key. `catalogSku` is
 * unique per real captured row (a configurable row's SKU is its family
 * template prefix) — falls back to the sourceable `id` for the rare row with
 * no SKU on file, never a synthesized index (ADR-0090/0091).
 */
function rowKey(card: CatalogResultCard): string {
  return card.sourceable.catalogSku || card.sourceable.id
}

/**
 * Whether a row's `configSchema` has at least one real dimension to pick
 * (ADR-0099 Gap 2's "shape correction": a series mixes fully-resolved
 * `catalog` rows with OPEN `configurable`-family rows). A `configSchema`
 * that is present but carries zero pickable dimension segments — or isn't
 * `resolvable` — is, for this row's purposes, already fully resolved: there
 * is nothing left to configure, so it expands like a plain catalog row
 * rather than into `ConfiguratorCore`'s picker/disabled-note UI (which this
 * page reserves for families that genuinely have an open dimension).
 */
function hasOpenDimensions(card: CatalogResultCard): boolean {
  if (!card.configSchema) return false
  return gateConfigSchema(card.configSchema).kind === 'pickable'
}

export function CatalogSeriesConfigurePage({
  service,
  seriesListService,
}: CatalogSeriesConfigurePageProps) {
  const { category = '', seriesCode = '' } = useParams<{ category: string; seriesCode: string }>()
  // 2026-09-03 series-card + drawer configurator pilot (see
  // `catalogTaxonomy.ts`'s `CARD_CONFIGURATOR_CATEGORIES` doc) — an opt-IN
  // allowlist of leaf categories that trade the flat SKU-line-item `<Table>`
  // below for a `ResultCard` grid whose own "Configure"/"Add" CTA opens the
  // shared `ConfiguratorDrawer`/`ConfiguratorCore` (same component already
  // wired from the search grid), instead of expanding the row in place into
  // `ConfiguratorCore` here. Every other category, including Linear Shafts,
  // is unaffected — this is computed once from the route param, not
  // per-row, since the WHOLE page's layout switches, not individual rows.
  const useCardLayout = usesCardConfiguratorLayout(category)
  const state = useCatalogSeriesRows(seriesCode, service)
  const [expandedKey, setExpandedKey] = useState<string | null>(null)
  const [selection, setSelection] = useState<SeriesFilterSelection>(EMPTY_SELECTION)
  const [searchParams, setSearchParams] = useSearchParams()

  // UID-387 follow-up — the category-page series grid's facet selection
  // (e.g. `Shaft Style: Hollow`), carried over via the SAME `specs[<name>]=
  // <value>` URL convention `CatalogSeriesPage` reads/writes, so a filtered
  // "View types" click doesn't land here showing the series' full
  // unfiltered row list. Read from THIS page's own URL, not re-derived from
  // any parent-page state (there is none to read across a navigation).
  const carriedSelection = useMemo(() => parseFacetSelection(searchParams), [searchParams])
  const hasCarriedSelection = Object.keys(carriedSelection).length > 0

  function clearCarriedSelection() {
    setSearchParams(facetSearchParams(searchParams, {}))
  }

  const firstRow = state.status === 'ready' ? state.rows[0] : undefined
  const rowDerivedHeading = firstRow ? resultDisplayName(firstRow.sourceable) : category
  // UID-521 — the row-derived name above is a generic PER-ROW product name
  // (identical across every series in a category), never the real per-
  // series name. `CatalogSeries.name` (fetched from the same series-list
  // seam `CatalogSeriesPage` already uses) is authoritative; fall back to
  // the row-derived name while that fetch is loading/erroring, or if this
  // page's `seriesCode` genuinely isn't in the returned list, rather than
  // regress to a blank/broken heading.
  const seriesListState = useCatalogSeries(category, undefined, seriesListService)
  const matchedSeries =
    seriesListState.status === 'ready'
      ? seriesListState.series.find((series) => series.seriesCode === seriesCode)
      : undefined
  const heading = matchedSeries?.name ?? rowDerivedHeading

  const allRows = state.status === 'ready' ? state.rows : []
  const facets = useMemo(() => deriveSeriesFacets(allRows), [allRows])
  // The carried-over category facet and this page's own dimension-based
  // sidebar filter are genuinely separate axes (`specs` vs. `configSchema`
  // segments — see `catalog-carried-facet-filter.ts`'s module doc) that
  // compose by intersection: narrow by the carried selection first, then by
  // whatever the shopper picks in `CatalogSeriesFilterSidebar`.
  const carriedFilter = useMemo(
    () => filterRowsByCarriedFacets(allRows, carriedSelection),
    [allRows, carriedSelection],
  )
  const visibleRows = useMemo(
    () => filterSeriesRows(carriedFilter.visibleRows, selection),
    [carriedFilter.visibleRows, selection],
  )
  const dimensionFieldNames = facets.dimensionFacets.map((facet) => facet.fieldName)
  const columnCount = 2 + dimensionFieldNames.length

  // Client-only "Recently viewed" tracking, V2 capture — an expand of a row
  // with no OPEN dimensions left (`!hasOpenDimensions`) is a finalized SKU,
  // the same bar the render branch just below already applies to choose the
  // plain `ResultCard` "Add" path over `ConfiguratorCore`. Only fires on the
  // EXPAND transition, never on collapse — computed here (not inside the
  // `setExpandedKey` updater) to keep the state updater itself free of side
  // effects.
  function handleRowToggle(card: CatalogResultCard) {
    const key = rowKey(card)
    const isExpanding = expandedKey !== key
    setExpandedKey(isExpanding ? key : null)
    if (isExpanding && !hasOpenDimensions(card)) {
      recordCatalogRowView(card)
    }
  }

  return (
    <section className="catalog-storefront" aria-labelledby="catalog-series-configure-heading">
      <div className="catalog-series-breadcrumb">
        <Link to="/catalog">Catalog</Link>
        <span aria-hidden="true">/</span>
        <Link to={`/catalog/series/${encodeURIComponent(category)}`}>{category}</Link>
      </div>
      {hasCarriedSelection && state.status === 'ready' && (
        <CarriedFacetBanner
          sourceCategory={category}
          selection={carriedSelection}
          matchCount={carriedFilter.matchCount}
          totalCount={carriedFilter.totalCount}
          omittedCount={carriedFilter.omittedCount}
          onClear={clearCarriedSelection}
        />
      )}

      {state.status === 'error' && (
        <EmptyState
          title="We couldn’t load this series"
          detail={state.message}
          action={<Button onClick={state.retry}>Try again</Button>}
        />
      )}

      {state.status !== 'error' && (
        <div className="catalog-series-layout">
          <CatalogSeriesFilterSidebar
            facets={facets}
            selection={selection}
            onChange={setSelection}
          />
          <div className="catalog-series-content">
            <h1 id="catalog-series-configure-heading" className="catalog-storefront-title">
              {heading}
            </h1>
            {useCardLayout ? (
              <div className="catalog-main">
                {state.status === 'loading' && (
                  <output className="catalog-result-grid" aria-busy="true">
                    <span className="sr-only">Loading {heading}…</span>
                    {Array.from({ length: 4 }, (_, index) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: a fixed-count skeleton row has no stable identity to key by.
                      <Skeleton key={index} height={180} variant="block" />
                    ))}
                  </output>
                )}

                {state.status === 'ready' && state.rows.length === 0 && (
                  <EmptyState
                    title="Nothing on file for this series yet"
                    detail="We don’t have any products linked to this series yet."
                  />
                )}

                {state.status === 'ready' && state.rows.length > 0 && visibleRows.length === 0 && (
                  <EmptyState
                    title="No configurations match your filters"
                    detail="Try widening or clearing the filters in the sidebar."
                  />
                )}

                {state.status === 'ready' && visibleRows.length > 0 && (
                  <output className="catalog-result-grid" aria-live="polite">
                    <span className="sr-only">{`Showing ${visibleRows.length} product${visibleRows.length === 1 ? '' : 's'} for ${heading}`}</span>
                    {visibleRows.map((card) => (
                      <ResultCard key={rowKey(card)} card={card} />
                    ))}
                  </output>
                )}
              </div>
            ) : (
              <Table
                className="catalog-main"
                caption={`Representative configurations for ${heading}`}
                columnCount={columnCount}
                busy={state.status === 'loading'}
              >
                <Table.Head>
                  <Table.Row>
                    <Table.HeaderCell>Name / SKU</Table.HeaderCell>
                    {dimensionFieldNames.map((fieldName) => (
                      <Table.HeaderCell key={fieldName}>{fieldName}</Table.HeaderCell>
                    ))}
                    <Table.HeaderCell align="end">Price (USD)</Table.HeaderCell>
                  </Table.Row>
                </Table.Head>
                <Table.Body>
                  {state.status === 'loading' && <Table.LoadingRows rows={4} />}

                  {state.status === 'ready' && state.rows.length === 0 && (
                    <Table.Empty
                      title="Nothing on file for this series yet"
                      detail="We don’t have any products linked to this series yet."
                    />
                  )}

                  {state.status === 'ready' &&
                    state.rows.length > 0 &&
                    visibleRows.length === 0 && (
                      <Table.Empty
                        title="No configurations match your filters"
                        detail="Try widening or clearing the filters in the sidebar."
                      />
                    )}

                  {state.status === 'ready' &&
                    visibleRows.map((card) => (
                      <SeriesRow
                        key={rowKey(card)}
                        card={card}
                        dimensionFieldNames={dimensionFieldNames}
                        columnCount={columnCount}
                        expanded={expandedKey === rowKey(card)}
                        onToggle={() => handleRowToggle(card)}
                      />
                    ))}
                </Table.Body>
              </Table>
            )}
          </div>
        </div>
      )}
    </section>
  )
}

/** Drops a moot `configSchema` (present but with no open dimension to pick)
 * before handing the row to `ResultCard`, so its price-row gating — which
 * treats ANY truthy `configSchema` as "needs Configure" — takes the plain
 * "Add" path instead. See the call site's comment for why this is honest,
 * not a fabrication: no real field's value changes, only which
 * already-resolved action renders. */
function asFullyResolvedCard(card: CatalogResultCard): CatalogResultCard {
  const { configSchema, ...rest } = card
  return rest
}

function formatPrice(card: CatalogResultCard): string {
  const price = card.sourceable.indicativePrice
  if (!price) return card.configSchema ? 'Configure for pricing' : 'Quote required'
  return `$${Number(price.amount).toFixed(2)}`
}

function SeriesRow({
  card,
  dimensionFieldNames,
  columnCount,
  expanded,
  onToggle,
}: {
  readonly card: CatalogResultCard
  readonly dimensionFieldNames: readonly string[]
  readonly columnCount: number
  readonly expanded: boolean
  readonly onToggle: () => void
}) {
  const key = rowKey(card)
  const name = resultDisplayName(card.sourceable)
  const panelId = `catalog-series-row-panel-${key}`

  return (
    <>
      <Table.Row>
        <Table.Cell>
          <button
            type="button"
            className="catalog-series-row-toggle"
            aria-expanded={expanded}
            aria-controls={panelId}
            onClick={onToggle}
          >
            <span className="catalog-series-row-toggle-text">
              <span className="catalog-series-row-name">{name}</span>
              {card.sourceable.catalogSku && (
                <span className="catalog-result-sku">{card.sourceable.catalogSku}</span>
              )}
            </span>
          </button>
        </Table.Cell>
        {dimensionFieldNames.map((fieldName) => (
          <Table.Cell key={fieldName}>{dimensionCellValue(card, fieldName)}</Table.Cell>
        ))}
        <Table.Cell align="end">{formatPrice(card)}</Table.Cell>
      </Table.Row>
      {expanded && (
        <tr id={panelId}>
          <td colSpan={columnCount} className="catalog-series-row-expanded">
            {/* UID-441 — card chrome (border/radius/shadow) lives on this
                inner `<div>`, not the `<td>` itself: a `<td>` inside
                `.data-table`'s `border-collapse: collapse` table doesn't
                honor `border-radius`/`box-shadow`, which was clipping the
                card's bottom corner (see `.catalog-series-row-expanded-card`
                in catalog.css). */}
            <div className="catalog-series-row-expanded-card">
              {hasOpenDimensions(card) && card.configSchema ? (
                <ConfiguratorCore
                  familyName={name}
                  schema={card.configSchema}
                  imageRef={card.sourceable.imageRef ?? null}
                />
              ) : (
                // A `configSchema` that IS present but has no open dimension
                // (resolvable, all-literal) is — for `ResultCard`'s price-row
                // gating — indistinguishable from a genuinely open family
                // unless stripped: `ResultCard` shows "Configure" for ANY
                // truthy `configSchema`. Since this row has nothing left to
                // pick, it belongs on the plain "Add" path; omitting the
                // (moot) schema here is honest — nothing about the row's
                // real fields changes, only which already-resolved action it
                // renders.
                <ResultCard card={asFullyResolvedCard(card)} layout="row" />
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}
